<script setup lang="ts">
import { faChevronLeft, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ComponentPublicInstance, MaybeRefOrGetter } from 'vue';
import { faLocationDot, faMapPin } from '@fortawesome/pro-solid-svg-icons';
import SearchFormWrapper from '@components/util/search/SearchFormWrapper.vue';
import CitySelect from '@components/util/search/CitySelect.vue';
import PaxPicker from '@components/util/search/PaxPicker.vue';
import type { AddEventListener, Radio } from '#imports';
import SearchDatePicker from '@components/util/search/SearchDatePicker.vue';
import { TripType } from '@utils/trip';
import {
    type DateSpanObject,
    type PaxCount,
    type SearchMode,
    type Location,
    type PaxCapacityInfo,
} from '@utils/types';
import { type ReserveType } from '@utils/itinerary';
import { type LocationGroup, type PaxModel } from '@search/types';
import { type LabeledPriceObject } from '@search/ItineraryPriceLoader';

type RefType = ComponentPublicInstance<{
    focus: Function;
    addEventListener: AddEventListener;
    hide: () => void;
}> | null;

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: Array<string>;

    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;

    prompt(): Promise<void>;
}

const props = defineProps<{
    isChangeSearch?: boolean;
    swapDisabled?: MaybeRefOrGetter<boolean | undefined>;
    tripTypeOptions?: MaybeRefOrGetter<Radio<TripType>[] | undefined>;
    reserveType: ReserveType;
    searchModeOptions?: Radio<SearchMode>[];
    searchMode: MaybeRefOrGetter<SearchMode>;
    tripType?: MaybeRefOrGetter<TripType | undefined>;
    formAlert?: { title: string; href: string };
    hideTypeSelector?: boolean;
    origin?: MaybeRefOrGetter<Location | null | undefined>;
    destination: MaybeRefOrGetter<Location | null>;
    isLoadingLocations?: MaybeRefOrGetter<boolean | undefined>;
    loading?: MaybeRefOrGetter<boolean | undefined>;
    loadingPrice?: MaybeRefOrGetter<boolean | undefined>;
    origins?: MaybeRefOrGetter<LocationGroup[] | undefined>;
    destinations: MaybeRefOrGetter<LocationGroup[]>;
    originTitle?: string;
    destinationTitle: string;
    date: MaybeRefOrGetter<DateSpanObject | null>;
    searchPaxModel?: MaybeRefOrGetter<PaxModel[] | undefined>;
    prices?: MaybeRefOrGetter<LabeledPriceObject[] | undefined>;
    emptyDestinationMessage?: string;
    locationDebounceSearch?: boolean;
    destinationIcon?: IconDefinition;
    destinationShowGroupTitle?: boolean;
    capacityInfo?: MaybeRefOrGetter<PaxCapacityInfo | undefined>;
    datepickerOptions?: {
        forceRange?: boolean;
        startTitle?: string;
        endTitle?: string;
        unequalRange?: boolean;
        desktopInRow?: boolean;
        countNights?: boolean;
        disableJoined?: boolean;
    };
    paxPickerOptions?: {
        maxAlert?: string;
        prefix?: string;
        unit?: string;
        disabled?: boolean;
        hidden?: boolean;
    };
    paxErrorMessage?: MaybeRefOrGetter<string | undefined>;
    onSubmit: () => Promise<boolean>;
}>();

const emit = defineEmits<{
    forceReload: [];
    swap: [];
    'update:tripType': [tripType: TripType];
    'update:searchMode': [searchMode: SearchMode];
    'update:origin': [origin: Location | null];
    'update:destination': [destination: Location | null];
    'update:isLoadingLocations': [loading: boolean];
    'update:date': [date: DateSpanObject | null];
    dateSubmit: [];
    search: [token: string, source: 'origin' | 'destination'];
    pageChange: [date: Date];
    'update:paxModel': [item: PaxCount, value: number, index: number];
    delete: [index: number];
}>();
const route = useRoute();
const gtm = useGtm();
const analyticsStore = useAnalyticsStore();

const tripTypeModel = computed({
    get(): TripType {
        return toValue(props.tripType) || TripType.OneWay;
    },
    set(value: TripType) {
        emit('update:tripType', value);
    },
});
const searchModeModel = computed({
    get(): SearchMode {
        return toValue(props.searchMode);
    },
    set(value: SearchMode) {
        emit('update:searchMode', value);
    },
});
const originModel = computed({
    get(): Location | null {
        return toValue(props.origin) ?? null;
    },
    set(value: Location | null) {
        emit('update:origin', value);
    },
});
const destinationModel = computed({
    get(): Location | null {
        return toValue(props.destination);
    },
    set(value: Location | null) {
        emit('update:destination', value);
    },
});
const isLoadingLocationsModel = computed({
    get(): boolean {
        return toValue(props.isLoadingLocations) || false;
    },
    set(value: boolean) {
        emit('update:isLoadingLocations', value);
    },
});
const dateModel = computed({
    get(): DateSpanObject | null {
        return toValue(props.date);
    },
    set(val: DateSpanObject | null) {
        emit('update:date', val);
    },
});
const swapDisabled = computed(() => toValue(props.swapDisabled) || false);
const searchMode = computed(() => toValue(props.searchMode));
const tripType = computed(() => toValue(props.tripType));
const loading = computed(() => toValue(props.loading));
const loadingPrice = computed(() => toValue(props.loadingPrice));
const searchModeOptions = computed(
    () => toValue(props.searchModeOptions) || []
);
const tripTypeOptions = computed(() => toValue(props.tripTypeOptions) || []);
const origins = computed(() => toValue(props.origins));
const destinations = computed(() => toValue(props.destinations));
const paxErrorMessage = computed(() => toValue(props.paxErrorMessage));
const capacityInfo = computed(() => toValue(props.capacityInfo));
const prices = computed(() => toValue(props.prices));
const searchPaxModel = computed(() => toValue(props.searchPaxModel));
const { isMobile } = useSize();
const originPicker = ref<RefType>(null);
const destinationPicker = ref<RefType>(null);
const departureDatePicker = ref<RefType>(null);
const countPicker = ref<RefType>(null);
let currentActiveWindow: RefType | null = null;
let historyStack: RefType[] = [];
watch(historyStack, async val => {
    if (!val.length) {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            deferredPrompt = null;
        }
    }
});
const handlePrompt = (e: Event) => {
    if (!deferredPrompt && historyStack.length) {
        e.preventDefault();
        deferredPrompt = e as BeforeInstallPromptEvent;
    }
};
let deferredPrompt: null | BeforeInstallPromptEvent = null;
const onBack = () => {
    if (currentActiveWindow) {
        currentActiveWindow.hide();
        currentActiveWindow = historyStack.pop() || null;
        if (currentActiveWindow) {
            currentActiveWindow.focus();
        } else {
            clearAll();
        }
    }
};
const handlePopState = () => {
    history.pushState({ stopNavigation: true }, '', '');
    onBack();
};
const clearAll = () => {
    historyStack = [];
    if (isMobile.value) {
        if ((history.state as any).stopNavigation) {
            // history.back();
        }
        window.removeEventListener('popstate', handlePopState);
    }
};
const modalOpened = (ref: RefType) => {
    if (isMobile) {
        if (!(history.state as any).stopNavigation) {
            history.pushState({ stopNavigation: true }, '', '');
        }
        window.addEventListener('popstate', handlePopState);
        currentActiveWindow = ref;
    }
};

onMounted(() => {
    if (!route.params.code && !props.isChangeSearch) {
        nextTick(() => {
            useCurrentSearchForm().value.loadLastSearch(route.path === '/');
        });
    }
    const refs = [
        originPicker,
        destinationPicker,
        departureDatePicker,
        countPicker,
    ];
    refs.forEach((ref, i) => {
        if (ref.value) {
            ref.value.addEventListener('done', () => {
                let focused = false;
                for (let j = i + 1; j < refs.length && !focused; j++) {
                    const next = refs[j].value;
                    if (!next || !next.focus) {
                        continue;
                    }
                    historyStack.push(ref.value);
                    focused = next.focus();
                }

                if (!focused) {
                    clearAll();
                }
            });
            ref.value.addEventListener('open', () => modalOpened(ref.value));
            ref.value.addEventListener('btn-close', () => clearAll());
        }
    });
    window.addEventListener('beforeinstallprompt', handlePrompt);
});
onBeforeUnmount(() => {
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('beforeinstallprompt', handlePrompt);
});

const emitSubmit = () => {
    // timeout used to run this code separately
    setTimeout(() => {
        if (props.isChangeSearch) {
            gtm?.trackEvent({ event: 'change-search' });
            gtm?.trackEvent({
                event: 'change-search button click',
            });
        } else {
            gtm?.trackEvent({ event: 'search' });
            gtm?.trackEvent({
                event: 'hp-search button click',
            });
        }
        analyticsStore.trackPosthog('search-form-submit', {
            inputCount: analyticsStore.searchInputSearchCount,
            reserveType: props.reserveType,
            isChangeSearch: props.isChangeSearch,
            searchMode,
        });
    });
    // then used to function get to the end sooner
    props.onSubmit().then(result => {
        if (!result) {
            emit('forceReload');
        }
    });
};
</script>
<template>
    <SearchFormWrapper
        :id="`search-${reserveType}`"
        :key="`search-${reserveType}`"
        :data-cat="`${reserveType}-search-filters`"
        :swap-disabled
        :hide-header="isChangeSearch"
        :is-change-search
        :search-mode
        :reserve-type
        :loading
        @submit="emitSubmit"
        @swap="emit('swap')"
        @force-reload="emit('forceReload')"
    >
        <template #header>
            <template v-if="searchModeOptions.length">
                <slot name="mode-selector">
                    <mr-radio-button
                        v-model="searchModeModel"
                        row
                        :name="`${reserveType}-options`"
                        size="large"
                        bold-label
                        :radios="searchModeOptions"
                    />
                </slot>
            </template>
            <mr-divider
                v-if="
                    tripTypeOptions.length > 1 &&
                    searchModeOptions.length &&
                    !hideTypeSelector
                "
                vertical
                size="20px"
                class="desktop-only mr-divider"
            />
            <mr-radio-button
                v-show="tripTypeOptions.length > 1 && !hideTypeSelector"
                v-model="tripTypeModel"
                size="large"
                bold-label
                name="trip-type"
                class="desktop-only"
                :radios="tripTypeOptions"
                row
            />
            <mr-dropdown
                v-if="tripTypeOptions.length > 1 && !hideTypeSelector"
                size="medium"
                :title="
                    tripTypeOptions.find(val => val.value === tripType)?.label
                "
                class="mobile-only"
                variant="transparent"
            >
                <template #content>
                    <mr-select-item
                        v-for="option in tripTypeOptions"
                        :key="option.value"
                        :item="{
                            id: option.value,
                            label: option.label,
                        }"
                        @click="tripTypeModel = option.value"
                    />
                </template>
            </mr-dropdown>
            <ClientOnly>
                <div
                    v-if="props.formAlert"
                    class="auto-reserve-button-wrapper desktop"
                >
                    <mr-button
                        variant="transparent"
                        :right-icon="{ icon: faSpinner, pulse: true }"
                        :left-icon="faChevronLeft"
                        link="nuxt"
                        :href="props.formAlert.href"
                    >
                        {{ props.formAlert.title }}
                    </mr-button>
                </div>
                <template #fallback />
            </ClientOnly>
        </template>
        <template v-if="origins" #origin>
            <CitySelect
                ref="originPicker"
                v-model="originModel"
                v-model:loading="isLoadingLocationsModel"
                :options="origins"
                :placeholder="originTitle"
                :icon="faMapPin"
                :debounce-search="locationDebounceSearch"
                :log-analyse-service-type="reserveType"
                :search-mode
                :reserve-type
                @search="emit('search', $event, 'origin')"
            />
        </template>
        <template #destination>
            <CitySelect
                ref="destinationPicker"
                v-model="destinationModel"
                v-model:loading="isLoadingLocationsModel"
                :options="destinations"
                :placeholder="destinationTitle"
                :empty-list-message="emptyDestinationMessage"
                :debounce-search="locationDebounceSearch"
                :icon="destinationIcon || faLocationDot"
                :show-group-titles="destinationShowGroupTitle"
                :search-mode
                :reserve-type
                :log-analyse-service-type="reserveType"
                @search="emit('search', $event, 'destination')"
            />
        </template>
        <template #date-picker>
            <SearchDatePicker
                ref="departureDatePicker"
                v-model="dateModel"
                :prices
                :loading-price
                :search-mode
                :reserve-type
                :joined="!datepickerOptions || !datepickerOptions.disableJoined"
                :range="
                    datepickerOptions?.forceRange ||
                    tripType === TripType.RoundTrip
                        ? 'enabled'
                        : 'disabled'
                "
                :can-change-range="
                    !datepickerOptions || !datepickerOptions.forceRange
                "
                :start-day-title="datepickerOptions?.startTitle"
                :end-day-title="datepickerOptions?.endTitle"
                :unequal-range="datepickerOptions?.unequalRange"
                :desktop-in-row-inputs="datepickerOptions?.desktopInRow"
                :count-nights="datepickerOptions?.countNights"
                @page-change="emit('pageChange', $event)"
                @submit="emit('dateSubmit')"
                @update:range="
                    tripTypeModel =
                        $event === 'disabled'
                            ? TripType.OneWay
                            : TripType.RoundTrip
                "
            />
        </template>
        <template
            v-if="
                searchPaxModel &&
                (!paxPickerOptions || !paxPickerOptions.hidden)
            "
            #pax-picker
        >
            <PaxPicker
                ref="countPicker"
                :paxes="searchPaxModel"
                :paxes-max-alert="paxPickerOptions?.maxAlert"
                :prefix="paxPickerOptions?.prefix"
                :disabled="paxPickerOptions?.disabled"
                :unit="paxPickerOptions?.unit"
                :reserve-type
                :search-mode
                :capacity-info
                :pax-error-message
                @submit="props.onSubmit()"
                @update:pax-model="
                    (item, value, i) => emit('update:paxModel', item, value, i)
                "
                @delete="emit('delete', $event)"
            >
                <template v-if="$slots['pax-picker-content']" #default>
                    <slot name="pax-picker-content" />
                </template>
            </PaxPicker>
        </template>
        <template v-if="$slots['submit-button']" #submit-button="attrs">
            <slot name="submit-button" v-bind="{ ...attrs }" />
        </template>
        <template #watermelon="{ classname }">
            <slot name="watermelon" :classname />
        </template>

        <ClientOnly>
            <div
                v-if="props.formAlert"
                class="auto-reserve-button-wrapper mobile"
            >
                <mr-button
                    variant="transparent"
                    :right-icon="{ icon: faSpinner, pulse: true }"
                    :left-icon="faChevronLeft"
                    link="nuxt"
                    full-width
                    :href="props.formAlert.href"
                >
                    {{ props.formAlert.title }}
                </mr-button>
            </div>
        </ClientOnly>
    </SearchFormWrapper>
</template>
<style scoped lang="scss">
.mr-divider {
    margin-inline: 20px;
}
.auto-reserve-button-wrapper {
    flex-grow: 1;
    display: flex;
    justify-content: flex-end;
    @include mobile-only {
        margin-top: 12px;
    }
    &.mobile {
        @include desktop-only {
            display: none;
        }
    }
    &.desktop {
        @include mobile-only {
            display: none;
        }
    }
}
</style>
